import { RevealBlock } from "./motion";
import { GraduationCap, Clock3, HandCoins, LineChart, ShieldCheck, Sparkles } from "lucide-react";

const OUTCOMES = [
  {
    icon: Clock3,
    t: "Access beyond shop hours",
    d: "Affordable essentials remain available around evening classes, study periods and residential quiet hours.",
  },
  {
    icon: HandCoins,
    t: "Low-risk pilot",
    d: "One compact, cashless unit with the final site and commercial structure agreed with the site operator.",
  },
  {
    icon: LineChart,
    t: "Evidence for the next step",
    d: "Transactions, category demand, availability, uptime and student feedback are reviewed throughout the trial.",
  },
];

const RANGE = [
  "Period care",
  "Dental & personal care",
  "Skincare & hygiene",
  "First-aid basics",
  "Study comfort",
  "Family essentials",
];

export default function CampusPilot() {
  const smoothTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="campus-pilot" className="relative bg-cream text-ink py-16 md:py-24" data-testid="campus-pilot-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <RevealBlock className="col-span-12 lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange/30 bg-orange/10 px-4 py-2 text-orange-deep text-xs font-semibold tracking-wide">
              <GraduationCap size={16} strokeWidth={1.8} />
              Campus & Resident Pilot
            </div>
            <h2 className="mt-5 font-display font-light text-ink text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Student essentials,<br /><span className="text-orange">available when needed.</span>
            </h2>
            <p className="mt-6 text-ink/65 text-base md:text-lg leading-relaxed max-w-2xl">
              A proposed 12-week pilot gives students and residents convenient access to personal-care, hygiene and wellbeing essentials while giving university or accommodation teams a clear, measurable way to assess demand.
            </p>
          </RevealBlock>

          <RevealBlock delay={0.08} className="col-span-12 lg:col-span-5">
            <div className="rounded-2xl bg-ink text-bone p-6 md:p-8 shadow-[0_18px_50px_rgba(10,30,68,0.16)]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-orange/15 flex items-center justify-center">
                  <Sparkles size={19} strokeWidth={1.7} className="text-orange" />
                </div>
                <div>
                  <p className="text-orange text-xs font-semibold tracking-wide">INDICATIVE RANGE</p>
                  <p className="text-bone/60 text-sm mt-1">Affordable, practical and adapted to student and resident need</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {RANGE.map((item) => (
                  <div key={item} className="rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm text-bone/85">
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-start gap-3 border-t border-white/10 pt-5">
                <ShieldCheck size={18} strokeWidth={1.7} className="text-orange mt-0.5 shrink-0" />
                <p className="text-bone/60 text-xs leading-relaxed">
                  Final products remain subject to site approval, supplier availability and product-level compliance review.
                </p>
              </div>
            </div>
          </RevealBlock>
        </div>

        <div className="mt-10 md:mt-14 grid md:grid-cols-3 gap-4 md:gap-5">
          {OUTCOMES.map((item, i) => {
            const Icon = item.icon;
            return (
              <RevealBlock key={item.t} delay={i * 0.06} y={20} className="bg-white border border-ink/10 rounded-2xl p-5 md:p-6 h-full shadow-[0_8px_28px_rgba(10,30,68,0.05)]">
                <div className="w-12 h-12 rounded-full bg-orange/15 flex items-center justify-center">
                  <Icon size={20} strokeWidth={1.7} className="text-orange-deep" />
                </div>
                <h3 className="mt-5 text-ink text-lg font-semibold leading-tight">{item.t}</h3>
                <p className="mt-2 text-ink/60 text-sm leading-relaxed">{item.d}</p>
              </RevealBlock>
            );
          })}
        </div>

        <RevealBlock delay={0.12} className="mt-10 md:mt-12 flex flex-col md:flex-row md:items-center justify-between gap-5 border-t border-ink/10 pt-8">
          <div>
            <p className="text-ink font-semibold">Proposed structure: one unit · 12 weeks · monthly reporting</p>
            <p className="mt-1 text-ink/55 text-sm">Location, access, responsibilities and commercial terms are agreed before launch.</p>
          </div>
          <button
            onClick={() => smoothTo("#enquiries")}
            className="group inline-flex items-center justify-center gap-2 h-12 px-7 bg-orange text-ink hover:bg-orange-soft rounded-full text-[13px] font-semibold transition-colors whitespace-nowrap"
            data-testid="campus-pilot-cta"
          >
            Discuss a Student Pilot
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </button>
        </RevealBlock>
      </div>
    </section>
  );
}
